import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { Card, Field, Input, Textarea, Select, PageHeader, PrimaryButton, DangerButton } from "@/components/admin/AdminUI";
import { SlugConflictWarning } from "@/components/admin/SlugConflictWarning";
import { CloudinaryUploader } from "@/components/CloudinaryUploader";
import { slugify } from "@/lib/slugify";
import { X, Columns2, LayoutPanelLeft, Images, AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd } from "lucide-react";
import MDEditor, { commands as mdCommands } from "@uiw/react-md-editor";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { rehypeGroupCmsRowText } from "@/lib/rehypeGroupCmsRowText";
import { SeoGenerateButton } from "@/components/admin/SeoGenerateButton";

/**
 * Custom MDEditor toolbar buttons that wrap the current selection in a layout
 * helper div. Falls back to a placeholder if nothing is selected.
 */
const wrapWithDiv = (className, placeholder) => ({ name, icon }) => ({
  name,
  keyCommand: name,
  buttonProps: { "aria-label": name, title: name },
  icon,
  execute: (state, api) => {
    const selected = state.selectedText || placeholder;
    api.replaceSelection(
      `\n<div class="${className}">\n\n${selected}\n\n</div>\n`
    );
  },
});

const twoColumnsCommand = wrapWithDiv(
  "cms-columns",
  "Your text will flow across two columns. Replace this with the paragraphs you want side-by-side."
)({
  name: "Two columns",
  icon: <Columns2 className="w-4 h-4" strokeWidth={2.25} />,
});

const sideBySideCommand = wrapWithDiv(
  "cms-row",
  "![alt text](https://your-image-url.png)\n\nYour text on the right side of the image."
)({
  name: "Image + text — align top",
  icon: <AlignVerticalJustifyStart className="w-4 h-4" strokeWidth={2.25} />,
});

const sideBySideCenterCommand = wrapWithDiv(
  "cms-row cms-row-center",
  "![alt text](https://your-image-url.png)\n\nYour text on the right side of the image."
)({
  name: "Image + text — align center",
  icon: <AlignVerticalJustifyCenter className="w-4 h-4" strokeWidth={2.25} />,
});

const sideBySideBottomCommand = wrapWithDiv(
  "cms-row cms-row-bottom",
  "![alt text](https://your-image-url.png)\n\nYour text on the right side of the image."
)({
  name: "Image + text — align bottom",
  icon: <AlignVerticalJustifyEnd className="w-4 h-4" strokeWidth={2.25} />,
});

/* Group the 3 alignment variants under a single toolbar dropdown so the
   toolbar stays uncluttered. */
const imageTextGroup = mdCommands.group(
  [sideBySideCommand, sideBySideCenterCommand, sideBySideBottomCommand],
  {
    name: "Image + text side by side",
    groupName: "image-text-side-by-side",
    buttonProps: { "aria-label": "Image + text side by side", title: "Image + text side by side (choose alignment)" },
    icon: <LayoutPanelLeft className="w-4 h-4" strokeWidth={2.25} />,
  }
);

const twoImagesCommand = wrapWithDiv(
  "cms-row cms-row-images",
  "![left image](https://your-image-url-1.png)\n\n![right image](https://your-image-url-2.png)"
)({
  name: "Two images side by side",
  icon: <Images className="w-4 h-4" strokeWidth={2.25} />,
});

const EMPTY = {
  title: "", slug: "", seo_title: "", seo_description: "", featured_image_url: "",
  content: "", status: "draft", is_public: true,
};

export default function AdminCmsEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const { data: page } = useQuery({ queryKey: ["cms-page", id], queryFn: async () => (await api.get(`/admin/cms-pages/${id}`)).data, enabled: !!id });
  useEffect(() => { if (page) setForm({ ...EMPTY, ...page }); }, [page]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.type === "checkbox" ? e.target.checked : (e?.target ? e.target.value : e) }));

  async function save() {
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/admin/cms-pages", form);
        toast.success("Page created");
        navigate(`/admin/cms-pages/${res.data.id}`);
      } else {
        await api.patch(`/admin/cms-pages/${id}`, form);
        toast.success("Saved");
        qc.invalidateQueries({ queryKey: ["cms-list"] });
        qc.invalidateQueries({ queryKey: ["cms-page", id] });
      }
    } catch (e) {
      const data = e?.response?.data;
      const det = data?.detail;
      if (det && typeof det === "object" && det.error === "slug_conflict") {
        const what = det.taken_by === "reserved" ? "a reserved system path" : det.taken_by === "brand" ? "another brand" : "another CMS page";
        toast.error(`URL "/${form.slug}" is already taken by ${what}${det.name ? ` ("${det.name}")` : ""}. Choose a different slug.`);
      } else {
        toast.error(typeof det === "string" ? det : "Save failed");
      }
    }
    finally { setSaving(false); }
  }

  async function remove() {
    if (!window.confirm("Delete this page?")) return;
    try {
      await api.delete(`/admin/cms-pages/${id}`);
      toast.success("Deleted");
      navigate("/admin/cms-pages");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  }

  return (
    <div data-color-mode="light">
      <PageHeader title={isNew ? "New page" : `Edit: ${form.title || "..."}`}
        actions={<>
          <PrimaryButton onClick={save} disabled={saving} data-testid="cms-save-button">{saving ? "Saving…" : "Save"}</PrimaryButton>
          {!isNew && <DangerButton onClick={remove}>Delete</DangerButton>}
        </>}
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 space-y-4">
          <Field label="Title"><Input value={form.title} onChange={set("title")} data-testid="cms-title-input" /></Field>
          <Field label="Slug" hint={isNew && !form.slug ? "Leave blank to auto-generate from title" : "URL: /your-slug"}>
            <Input value={form.slug || ""} onChange={set("slug")} placeholder={isNew ? slugify(form.title) : ""} />
            <SlugConflictWarning slug={form.slug || slugify(form.title)} kind="cms_page" ignoreId={id} />
          </Field>
          <Field
            label="Featured image"
            hint="Upload a file or paste a URL — both go through the same uploader (use the 'Web Address' tab inside the widget for URLs)."
          >
            <div className="flex items-center gap-3">
              <Input value={form.featured_image_url || ""} onChange={set("featured_image_url")} placeholder="https://…" data-testid="cms-featured-url-input" />
              <CloudinaryUploader
                mode="admin"
                folder="sustainably_curated/cms"
                multiple={false}
                label={form.featured_image_url ? "Replace" : "Upload"}
                onUploaded={(url) => setForm((f) => ({ ...f, featured_image_url: url }))}
              />
            </div>
            {form.featured_image_url && (
              <div className="mt-3 relative inline-block">
                <img src={form.featured_image_url} alt="Featured" className="h-28 w-auto rounded-md border border-[#E9ECEF] object-cover" />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, featured_image_url: "" }))}
                  className="absolute -top-2 -right-2 bg-white border border-[#E9ECEF] rounded-full p-1 shadow hover:bg-red-50 hover:border-red-200"
                  data-testid="cms-featured-clear"
                  aria-label="Remove featured image"
                >
                  <X className="w-3.5 h-3.5 text-red-600" />
                </button>
              </div>
            )}
          </Field>
          <Field label="Content (Markdown)">
            <div className="border border-[#E9ECEF] rounded-md overflow-hidden cms-md-editor">
              <MDEditor
                value={form.content}
                onChange={(v) => setForm((f) => ({ ...f, content: v || "" }))}
                height={500}
                preview="live"
                data-testid="cms-content-editor"
                previewOptions={{
                  rehypePlugins: [[rehypeRaw], rehypeGroupCmsRowText],
                  remarkPlugins: [remarkGfm],
                }}
                commands={[
                  mdCommands.bold,
                  mdCommands.italic,
                  mdCommands.strikethrough,
                  mdCommands.hr,
                  mdCommands.title,
                  mdCommands.divider,
                  mdCommands.link,
                  mdCommands.quote,
                  mdCommands.code,
                  mdCommands.codeBlock,
                  mdCommands.image,
                  mdCommands.divider,
                  twoColumnsCommand,
                  imageTextGroup,
                  twoImagesCommand,
                  mdCommands.divider,
                  mdCommands.unorderedListCommand,
                  mdCommands.orderedListCommand,
                  mdCommands.checkedListCommand,
                  mdCommands.divider,
                  mdCommands.help,
                ]}
              />
            </div>
            <details className="mt-2 text-xs text-[#6C757D]" open>
              <summary className="cursor-pointer select-none text-forest font-medium">Layout helpers — how the column buttons work</summary>
              <div className="mt-2 space-y-3 bg-[#F8F9FA] border border-[#E9ECEF] p-3 leading-relaxed">
                <p className="font-sans text-[12px] text-[#5C6A5A] m-0">
                  Select the text or images you want to lay out, then click one of the three highlighted buttons in the toolbar above (between the image and list icons):
                </p>
                <ul className="font-sans text-[12px] text-[#5C6A5A] list-disc pl-5 space-y-1 m-0">
                  <li><strong>Two columns</strong> — flow the selected text across two columns.</li>
                  <li><strong>Image + text side by side</strong> — click the dropdown arrow to choose <em>top</em>, <em>center</em>, or <em>bottom</em> alignment between the image and the text.</li>
                  <li><strong>Two images side by side</strong> — show two images on the same row.</li>
                </ul>
                <p className="font-sans text-[12px] text-[#5C6A5A] m-0">If nothing is selected, a starter template is inserted that you can edit in-place. All layouts collapse to a single column on mobile.</p>
                <div className="font-mono text-[11px]">
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1 font-sans">Raw syntax it inserts</div>
{`<div class="cms-columns">

Paragraph one flowing into column one and then continuing on into column two automatically.

</div>`}
                </div>
              </div>
            </details>
          </Field>
        </Card>
        <Card className="space-y-4 h-fit">
          <Field label="Status">
            <Select value={form.status} onChange={set("status")}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </Select>
          </Field>
          <div className="flex items-center justify-between"><span className="text-sm">Public</span><input type="checkbox" checked={!!form.is_public} onChange={set("is_public")} /></div>
          <div className="border-t border-[#E9ECEF] pt-3 flex items-center justify-between">
            <h3 className="text-xs uppercase tracking-wider text-[#6C757D]">SEO</h3>
            <SeoGenerateButton
              disabled={!form.title}
              buildPayload={() => ({
                kind: "cms_page",
                title: form.title,
                content: form.content,
              })}
              onResult={({ seo_title, seo_description }) =>
                setForm((f) => ({ ...f, seo_title, seo_description }))
              }
            />
          </div>
          <Field label="SEO title"><Input value={form.seo_title || ""} onChange={set("seo_title")} /></Field>
          <Field label="SEO description"><Textarea rows={3} value={form.seo_description || ""} onChange={set("seo_description")} /></Field>
        </Card>
      </div>
    </div>
  );
}
